/**
 * @file bugs.tab.tsx
 * @description Вкладка багов для формы задачи.
 * Отображает таблицу связанных с задачей багов с возможностью 
 * добавления новых и удаления существующих связей.
 * Автоматически пересчитывает плановые даты выполнения задачи при изменении списка багов.
 * @author AI Assistant
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, @src/types, @components/form-controls/table, @utils/table-view, @components/icons/trash-flat, @api/actions/method.service, @consta/uikit/useDebounce, @src/utils/date-time-helper]
 * @related-files [@components/form-controls/table/table.component.tsx, @utils/table-view.tsx, @api/actions/method.service.tsx, @utils/date-time-helper.ts]
 */

import { TErrorMessage, tabProps } from '@src/types';
import { Table } from '@components/form-controls/table';
import { getDefaultCell, getBadgeCell } from '@utils/table-view';
import { TrashFlatIcon } from '@components/icons/trash-flat';
import { MethodService } from '@api/actions/method.service';
import { useDebounce } from '@consta/uikit/useDebounce';
import { formatTimestamp } from '@src/utils/date-time-helper';
import { IColumnConfig } from '../../../types/pages';

export const BugsTab = ({ control, data, getValues, setValue, errorListRef }: tabProps) => {
    const onChange = async () => {
        if (!getValues || !setValue) {
            return;
        }

        const item = getValues();
        const response: any = await MethodService.getPlannedExecutionDate({
            executor: item.executor,
            laboriousnessPlan: item.laboriousnessPlan,
            predecessors: item.predecessors?.map((item: any) => item.$item.id),
            dateStartPlan: item.dateStartPlan
        });

        if (response.status !== 200 && response.data.errors?.length) {
            errorListRef?.current?.setItems(
                response.data.errors.map(({ message, title, status }: TErrorMessage, key: number) => ({
                    key,
                    message,
                    title,
                    status
                }))
            );
        }

        response.data.dateStartPlan && setValue('dateStartPlan', response.data.dateStartPlan);
        response.data.dateEndPlan && setValue('dateEndPlan', response.data.dateEndPlan);
    };
    const debounceChangeValues = useDebounce(onChange, 500);

    return (
        <Table
            columns={columns}
            addItemType="catalog"
            type="Bug"
            name="bugs"
            control={control}
            hideHeader={true}
            isCanAddRows={true}
            isRowClickable={true}
            contextMenuItemList={contextMenuItemList}
            excludeIds={data?.item?.id ? [{ $item: data.item }] : []}
            onChangeHandler={debounceChangeValues}
            isShowEmptyBody={true}
            className="dhx_grid_no_rows_between"
        />
    );
};

//TODO: пересмотреть подход к указанию элементов контекстного меню, возможно через отдельно свойство
const contextMenuItemList = [
    {
        label: 'Удалить',
        icon: <TrashFlatIcon className="text-[#B2C8D4] hover:text-[#99B8C9]" />,
        clickHandler: (row: any, rows: any[], onChange: any) => {
            onChange(rows.filter((item) => item.id !== row.id));
        },
        isOnlyIcon: true
    }
];

const columns: IColumnConfig[] = [
    {
        id: 'state',
        header: [{ text: '' }],
        mark: () => '!pl-0',
        width: 130,
        render: (cellValue: any) => getBadgeCell(cellValue)
    },
    {
        id: '$item',
        header: [{ text: '' }],
        width: 110,
        render: (cellValue: any) => getDefaultCell(cellValue, ' ')
    },
    {
        id: 'name',
        header: [{ text: '' }],
        render: (cellValue: any) => (
            <span className="flex w-full border-r pr-[0.8472vmin]">{getDefaultCell(cellValue)}</span>
        )
    },
    {
        id: 'dateBug',
        header: [{ text: '' }],
        width: 125,
        mark: () => '!pl-0',
        render: (cellValue: any) => (
            <span className="flex w-full border-r pr-[0.8472vmin]">
                {formatTimestamp(cellValue, 'DD.MM.YYYY HH:mm')}
            </span>
        )
    },
    {
        id: 'dateFix',
        header: [{ text: '' }],
        width: 125,
        mark: () => '!pl-0',
        render: (cellValue: any) => (
            <span className="flex w-full border-r pr-[0.8472vmin]">
                {formatTimestamp(cellValue, 'DD.MM.YYYY HH:mm')}
            </span>
        )
    },
    {
        id: 'executor',
        header: [{ text: '' }],
        width: 140,
        align: 'right',
        render: (cellValue: any) => getDefaultCell(cellValue)
    }
];
